import { useContext, useEffect, useState } from 'react';
import LayersContext from '../../context/LayersContext';
import { formatValue } from '../../utils/helpers';
import ItemCard from './ItemCard';
import searchSvg from '../../assets/search.svg';
// import { useNavigate } from 'react-router-dom';

interface LayerPanelProps {
    dashboardView: string;
}

const LayerPanel: React.FC<LayerPanelProps> = ({ dashboardView }) => {
    const { layers, items } = useContext(LayersContext);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [activeCardId, setActiveCardId] = useState<number | null>(null); // Track active card
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const itemsPerPage = 10;

    // Get the layer items from context
    const layerItems = items?.items;

    // Set the active layer based on dashboardView prop
    const activeLayer = layers.find(layer => layer.layer_name.toLowerCase() === dashboardView.toLowerCase())?.layer_name || null;

    // Set the active layer id
    const activeLayerId = layers.find(layer => layer.layer_name.toLowerCase() === dashboardView.toLowerCase())?.layer_id || null;

    // Reset current page to 1 any time the category, search or active layer changes
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategories, searchQuery, activeLayer]);

    // Filter layer items based on selected categories
    const filteredLayerItems = selectedCategories.length > 0
    ? layerItems?.filter(item => selectedCategories.includes(item.category))
    : layerItems;

    const searchedLayerItems = filteredLayerItems?.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort the filtered items by activity_date (descending)
    const sortedLayerItems = searchedLayerItems?.sort((a, b) => {
        const dateA = new Date(a.activity_date);
        const dateB = new Date(b.activity_date);
        return dateB.getTime() - dateA.getTime();
    });

    // Calculate the total value of the filtered items
    const totalValue = filteredLayerItems?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;

    // Paginate the filtered items
    const totalItems = filteredLayerItems?.length || 0;
    const totalSearchedItems = sortedLayerItems?.length || 0;
    const totalPages = Math.ceil(totalSearchedItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = sortedLayerItems?.slice(startIndex, startIndex + itemsPerPage);

    // Get unique categories from layerItems for the dropdown
    const categories = Array.from(new Set(layerItems?.map(item => item.category) || []));

    // Handle page change
    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return; // Prevent invalid page numbers
        setCurrentPage(page);
    };

    // Toggle category selection
    const handleCategoryToggle = (category: string) => {
        setSelectedCategories(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category) // Remove if already selected
                : [...prev, category] // Add if not selected
        );
    };

    const portOrigin = '*';

    const handleExpandClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        // Send message to expand
        window.parent.postMessage({ message: "expandLeftPanel" }, portOrigin);
        console.log('expand message sent');
    }

    return (
        <div>
            {/* Message when no active layer is selected */}
            {!activeLayer ? (
                <div className="mt-4 p-4 text-center text-gray-600">
                    <p>No active layer selected.</p>
                </div>
            ) : (
                <>
                    {/* Header */}
                    <div className='flex items-center justify-center bg-[#EFEFEF] h-12'>
                        <p className='text-sm font-bold text-port-text'>Port of Seattle Community Impacts</p>
                    </div>
                    <div className="grid grid-cols-3 p-6 pt-0 w-full">
                        {/* Text for no categories or items present */}
                        {layerItems.length === 0 &&
                        <>
                            <p className='col-span-3 mt-4 m-auto text-xs text-port-text'>There are no items from <strong>{activeLayer}</strong> within your current selection.</p>
                            <p className='col-span-3 mt-2 m-auto text-xs text-port-text'>The map is still fully interactive—try widening, moving, or clearing your selection to uncover nearby items.</p>
                        </>
                        }
                        {/* Category Dropdown */}
                        <div className="mt-4 flex flex-col col-span-2">
                            {categories.map((category, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleCategoryToggle(category)}
                                    className={`flex items-center gap-2 pr-3 mr-8 py-2 border-b text-left ${index === categories.length - 1 ? 'border-b-0' : ''}`}
                                >
                                    <span
                                        className={`w-4 h-4 flex items-center justify-center border border-port-green rounded ${selectedCategories.includes(category) ? "bg-port-green text-white" : "bg-white"
                                        }`}
                                    >
                                        {selectedCategories.includes(category) && <span>✔</span>}
                                    </span>
                                    <p className='text-xs font-bold text-port-text'>{category}</p>
                                </button>
                            ))}
                        </div>

                        {/* Totals */}
                        <div className="flex flex-col items-start justify-center gap-4 col-span-1">
                            <div className={`flex flex-col items-start justify-center ${totalItems ? 'visible' : 'invisible'}`}>
                                <p className="text-[44px] font-bold text-port-blue font-barlow-condensed">
                                    {formatValue(totalItems, 'integer')}
                                </p>
                                <p className='text-xs font-bold text-port-text -mt-2'>Total Projects</p>
                            </div>
                            <div className={`flex flex-col items-start justify-center ${totalValue ? 'visible' : 'invisible'}`}>
                                <p className="text-[44px] font-bold text-port-blue font-barlow-condensed">
                                    {`$${formatValue(totalValue, 'integer')}`}
                                </p>
                                <p className='text-xs font-bold text-port-text -mt-2'>Total Funding</p>
                            </div>
                        </div>

                        {/* Program Tour (storymap) */}
                        {layerItems.length > 0 ?
                        <div className='mt-4 flex justify-start col-span-2'>
                            <button onClick={handleExpandClick} className='border border-port-green bg-port-green h-10 px-2 py-1 rounded text-white text-[11px] font-bold'>
                                Click to Start Tour of Programs
                            </button>
                        </div> : null }

                        {/* Search Bar */}
                        {layerItems.length > 0 &&
                            <div className="col-span-3 flex items-center mt-4 border rounded-lg p-2 bg-white shadow-lg">
                                <input
                                    type="text"
                                    placeholder="Search for sites..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full outline-none px-2 text-xs text-port-text placeholder:text-port-green font-bold"
                                />
                                {searchQuery ? (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="mx-1 text-gray-500 hover:text-port-green text-xs font-bold"
                                    >
                                        ✕
                                    </button>
                                ) : (
                                    <img className="w-4 h-4" src={searchSvg} alt='Search icon'/>
                                )}
                            </div>
                        }
                        

                        {/* Item Cards */}
                        <div className="grid grid-cols-1 gap-4 mt-4 col-span-3">
                            {paginatedItems?.map((layerItem, index) => (
                                <ItemCard 
                                    key={index} 
                                    width="w-full" 
                                    layerItem={layerItem} 
                                    layerId={activeLayerId}
                                    isActive={activeCardId === layerItem.id}
                                    setActiveCardId={setActiveCardId} 
                                />
                            ))}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 0 && (
                            <div className="mt-4 flex justify-center gap-4 text-xs items-center col-span-3">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md disabled:opacity-50 w-24"
                                >
                                    Previous
                                </button>

                                <span className="text-gray-700">
                                    Page {currentPage} of {totalPages}
                                </span>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md disabled:opacity-50 w-24"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default LayerPanel;